/* Cart Commander storefront widgets (Phase 1).
 * Renders recommendation rows into .cc-widget[data-cc-widget] containers.
 * Endpoint host is set once here; widgets/config live in the Cart Commander admin.
 * Accessibility: tiles are <div> cards with a real link + sibling button (no
 * button-in-anchor nesting), escaped titles/alt text, carousel region +
 * labelled arrows, sr-only price context, polite add-to-cart announcements,
 * and reduced-motion-aware scrolling. */
(function () {
  "use strict";
  // Each CC theme section includes this script tag; the browser executes every
  // tag, so guard against running init more than once (fixes duplicate rows).
  if (window.__ccWidgetsLoaded) return;
  window.__ccWidgetsLoaded = true;
  let CC_HOST = "https://glorious-crow-287.convex.site"; // replaced at install, e.g. https://xxx.convex.site
  let RV_KEY = "cc:recently-viewed";
  let seen = typeof IntersectionObserver !== "undefined";

  function money(v) {
    let n = Number(v);
    // Whole-dollar display rule (Darren, 2026-08-02): prices round UP, never cents.
    return isNaN(n) ? "" : "$" + Math.ceil(n).toLocaleString("en-US");
  }

  // HTML-escape untrusted strings (product titles etc.) before innerHTML use.
  function esc(s) {
    var d = document.createElement("div");
    d.textContent = s == null ? "" : String(s);
    return d.innerHTML;
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  // Shared polite live region so screen readers hear add-to-cart feedback.
  var liveRegion = null;
  function announce(text) {
    try {
      if (!liveRegion) {
        liveRegion = document.createElement("div");
        liveRegion.className = "cc-sr";
        liveRegion.setAttribute("role", "status");
        liveRegion.setAttribute("aria-live", "polite");
        document.body.appendChild(liveRegion);
      }
      liveRegion.textContent = "";
      setTimeout(function () {
        liveRegion.textContent = text;
      }, 50);
    } catch (e) {}
  }

  function beacon(widget, type, valueCents) {
    try {
      var payload = { widget: widget, type: type };
      if (valueCents != null) payload.valueCents = valueCents;
      navigator.sendBeacon(
        CC_HOST + "/storefront/event",
        // text/plain keeps this a CORS "simple" request: an application/json
        // beacon triggers a credentialed preflight that browsers reject, which
        // silently dropped every impression/click event (fixed 2026-10-15).
        new Blob([JSON.stringify(payload)], { type: "text/plain;charset=UTF-8" })
      );
    } catch (e) {}
  }

  // Revenue attribution: hidden cart attribute _cc_<variantId> = widgetKey,
  // read by the orders/create webhook to credit the widget with revenue.
  function tagAttribution(variantId, widgetKey) {
    var attrs = {};
    attrs["_cc_" + variantId] = widgetKey;
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attrs }),
    }).catch(function () {});
  }

  // Which product elements a widget shows (all default ON except the
  // Add to Cart button, which is opt-in). Configured per widget in the app.
  function elementFlags(style) {
    style = style || {};
    function on(v, dflt) {
      return v === undefined || v === null ? dflt : !!v;
    }
    return {
      image: on(style.showImage, true),
      title: on(style.showTitle, true),
      price: on(style.showPrice, true),
      compareAt: on(style.showCompareAt, true),
      addToCart: on(style.showAddToCart, false),
      addToCartLabel: style.addToCartLabel || "Add to Cart",
    };
  }

  function tile(p, widgetKey, els) {
    els = els || elementFlags(null);
    var safeTitle = esc(p.title);
    // Card wrapper: the product link and the Add to Cart button are SIBLINGS
    // (a button nested inside an anchor is invalid HTML and confuses
    // screen readers / keyboard navigation).
    var card = document.createElement("div");
    card.className = "cc-tile";
    var a = document.createElement("a");
    a.className = "cc-tile-link";
    a.href = p.url;
    a.addEventListener("click", function () {
      beacon(widgetKey, "click");
    });
    var img = !els.image
      ? ""
      : p.image
        ? '<img class="cc-tile-img" loading="lazy" src="' +
          esc(p.image) +
          '" alt="' +
          safeTitle +
          '">'
        : '<div class="cc-tile-img cc-tile-noimg" role="presentation"></div>';
    var hasCompare =
      els.compareAt &&
      p.compareAtPrice &&
      Number(p.compareAtPrice) > Number(p.price);
    var compare = hasCompare
      ? '<s class="cc-compare"><span class="cc-sr">Original price </span>' +
        money(p.compareAtPrice) +
        "</s>"
      : "";
    var srSale = hasCompare ? '<span class="cc-sr">Sale price </span>' : "";
    var price = els.price
      ? '<div class="cc-tile-price">' +
        srSale +
        money(p.price) +
        " " +
        compare +
        "</div>"
      : compare
        ? '<div class="cc-tile-price">' + compare + "</div>"
        : "";
    // When the title element is hidden the link still needs an accessible
    // name beyond the image alt, so label the link with the product title.
    if (!els.title) a.setAttribute("aria-label", p.title);
    a.innerHTML =
      img +
      (els.title ? '<div class="cc-tile-title">' + safeTitle + "</div>" : "") +
      price;
    card.appendChild(a);
    if (els.addToCart && p.variantId) {
      let btn = document.createElement("button");
      btn.className = "cc-tile-atc";
      btn.type = "button";
      btn.textContent = els.addToCartLabel;
      btn.setAttribute("aria-label", els.addToCartLabel + ": " + p.title);
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
        beacon(widgetKey, "add_to_cart", Math.round(Number(p.price) * 100));
        fetch("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: Number(p.variantId), quantity: 1 }),
        })
          .then(function () {
            return tagAttribution(Number(p.variantId), widgetKey);
          })
          .then(function () {
            announce(p.title + " added to cart");
            // Open the Cart Commander slide cart if it's on the page.
            document.dispatchEvent(new CustomEvent("cc:open-cart"));
          })
          .catch(function () {})
          .finally(function () {
            btn.disabled = false;
          });
      });
      card.appendChild(btn);
    }
    return card;
  }

  var STYLE_VARS = {
    headingColor: "--cc-heading-color",
    headingSize: "--cc-heading-size",
    headingFont: "--cc-heading-font",
    headingAlign: "--cc-heading-align",
    fontFamily: "--cc-font-family",
    titleColor: "--cc-title-color",
    titleSize: "--cc-title-size",
    priceColor: "--cc-price-color",
    priceSize: "--cc-price-size",
    compareAtColor: "--cc-compare-color",
    cardBg: "--cc-card-bg",
    cardBorderColor: "--cc-card-border",
    cardHoverBorderColor: "--cc-card-hover-border",
    borderRadius: "--cc-radius",
    tileWidth: "--cc-tile-width",
    imageHeight: "--cc-image-height",
    imageWidth: "--cc-image-width",
    imageFit: "--cc-image-fit",
    imageAspect: "--cc-image-aspect",
    addToCartBg: "--cc-atc-bg",
    addToCartText: "--cc-atc-text",
    addToCartBorder: "--cc-atc-border",
    addToCartSize: "--cc-atc-size",
  };

  // Bare numbers in size-like values are treated as px so "225" just works.
  var PX_KEYS = {
    headingSize: 1, titleSize: 1, priceSize: 1, borderRadius: 1,
    tileWidth: 1, imageHeight: 1, imageWidth: 1, addToCartSize: 1,
  };
  function normalizeStyle(style) {
    if (!style) return style;
    Object.keys(PX_KEYS).forEach(function (k) {
      if (style[k] != null && /^\d+(\.\d+)?$/.test(String(style[k]).trim()))
        style[k] = String(style[k]).trim() + "px";
    });
    return style;
  }

  function applyStyle(el, style) {
    if (!style) return;
    style = normalizeStyle(style);
    Object.keys(STYLE_VARS).forEach(function (key) {
      if (style[key]) el.style.setProperty(STYLE_VARS[key], style[key]);
    });
    // An aspect ratio should drive the height unless one is set explicitly.
    if (style.imageAspect && !style.imageHeight)
      el.style.setProperty("--cc-image-height", "auto");
  }

  // Wrap a .cc-row in a carousel shell with arrow buttons on each end.
  // Arrows page by the visible width; they fade out at the ends and hide
  // entirely when everything already fits. Touch swiping still works.
  // The shell is a labelled region so screen-reader users can find it, and
  // end-of-track arrows are aria-disabled (kept focusable so focus never drops).
  function buildCarousel(row, label) {
    var shell = document.createElement("div");
    shell.className = "cc-carousel";
    shell.setAttribute("role", "region");
    shell.setAttribute("aria-roledescription", "carousel");
    shell.setAttribute("aria-label", label || "Product recommendations");
    function arrow(dir) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "cc-arrow cc-arrow-" + dir;
      b.setAttribute("aria-label", dir === "prev" ? "Previous products" : "More products");
      b.innerHTML =
        dir === "prev"
          ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="15 18 9 12 15 6"/></svg>'
          : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="9 18 15 12 9 6"/></svg>';
      b.addEventListener("click", function () {
        if (b.getAttribute("aria-disabled") === "true") return;
        var step = Math.max(row.clientWidth - 80, 160);
        row.scrollBy({
          left: dir === "prev" ? -step : step,
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
      });
      return b;
    }
    var prev = arrow("prev");
    var next = arrow("next");
    function sync() {
      var max = row.scrollWidth - row.clientWidth;
      if (max <= 4) {
        shell.classList.remove("cc-has-arrows");
        return;
      }
      shell.classList.add("cc-has-arrows");
      var prevOff = row.scrollLeft <= 4;
      var nextOff = row.scrollLeft >= max - 4;
      prev.classList.toggle("cc-arrow-off", prevOff);
      next.classList.toggle("cc-arrow-off", nextOff);
      prev.setAttribute("aria-disabled", prevOff ? "true" : "false");
      next.setAttribute("aria-disabled", nextOff ? "true" : "false");
    }
    var t;
    row.addEventListener("scroll", function () {
      clearTimeout(t);
      t = setTimeout(sync, 80);
    });
    window.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(sync, 120);
    });
    shell.appendChild(prev);
    shell.appendChild(row);
    shell.appendChild(next);
    requestAnimationFrame(sync);
    setTimeout(sync, 400); // after images/layout settle
    return shell;
  }

  function renderRow(el, widgetKey, data) {
    if (!data || !data.products || data.products.length === 0) return;
    applyStyle(el, data.style);
    let wrap = document.createElement("div");
    wrap.className = "cc-row-wrap";
    let h;
    if (data.title) {
      h = document.createElement("h2");
      h.className = "cc-row-title";
      h.textContent = data.title;
      wrap.appendChild(h);
    }
    let row = document.createElement("div");
    row.className = "cc-row";
    // Fluid tiles: the widget's max-items setting (or the section's override)
    // sets how many tiles share the full row width — 3 means three tiles
    // filling the widget, 4 means four, etc. Falls back to the actual count.
    var cols =
      Number(el.getAttribute("data-cc-limit")) ||
      Number(data.maxItems) ||
      data.products.length;
    if (cols > 0) row.style.setProperty("--cc-cols", cols);
    var els = elementFlags(data.style);
    data.products.forEach(function (p) {
      row.appendChild(tile(p, widgetKey, els));
    });
    wrap.appendChild(buildCarousel(row, data.title));
    el.appendChild(wrap);
    if (seen) {
      let io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            beacon(widgetKey, "impression");
            io.disconnect();
          }
        });
      });
      io.observe(el);
    } else {
      beacon(widgetKey, "impression");
    }
  }

  function recentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(RV_KEY) || "[]");
    } catch (e) {
      return [];
    }
  }

  function trackView(productId) {
    try {
      let list = recentlyViewed().filter(function (id) {
        return id !== productId;
      });
      list.unshift(productId);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 20)));
    } catch (e) {}
  }

  function init() {
    let containers = document.querySelectorAll(".cc-widget[data-cc-widget]");
    let pdpProduct = null;
    containers.forEach(function (el) {
      if (el.getAttribute("data-cc-product"))
        pdpProduct = el.getAttribute("data-cc-product");
    });
    if (pdpProduct) trackView(pdpProduct);

    containers.forEach(function (el) {
      // Belt and suspenders: never render into the same container twice.
      if (el.getAttribute("data-cc-rendered")) return;
      el.setAttribute("data-cc-rendered", "1");
      let key = el.getAttribute("data-cc-widget");
      let productId = el.getAttribute("data-cc-product");
      let limit = el.getAttribute("data-cc-limit");
      if (key === "recently-viewed") {
        let ids = recentlyViewed().filter(function (id) {
          return id !== pdpProduct;
        });
        if (ids.length === 0) return;
        // Config call first (enabled check + heading + styling), then hydrate.
        fetch(CC_HOST + "/storefront/recs?widget=recently-viewed")
          .then(function (r) {
            return r.json();
          })
          .then(function (cfg) {
            if (cfg && cfg.disabled) return;
            return fetch(CC_HOST + "/storefront/hydrate", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                gids: ids.slice(
                  0,
                  limit ? Number(limit) : (cfg && cfg.maxItems) || 12
                ),
              }),
            })
              .then(function (r) {
                return r.json();
              })
              .then(function (data) {
                renderRow(el, key, {
                  title:
                    el.getAttribute("data-cc-title") ||
                    (cfg && cfg.title) ||
                    "Recently Viewed",
                  style: cfg && cfg.style,
                  maxItems: cfg && cfg.maxItems,
                  products: data.products,
                });
              });
          })
          .catch(function () {});
        return;
      }
      // Personalized widgets send the visitor's recently viewed products as
      // seeds (most recent first); the server blends them against the
      // co-purchase/similarity matrix. Falls back to trending server-side.
      let seed = "";
      if (key === "for-you") {
        let ids = recentlyViewed().slice(0, 10);
        if (ids.length > 0) seed = "&seed=" + encodeURIComponent(ids.join(","));
      }
      let url =
        CC_HOST +
        "/storefront/recs?widget=" +
        encodeURIComponent(key) +
        (productId ? "&product=" + encodeURIComponent(productId) : "") +
        (limit ? "&limit=" + encodeURIComponent(limit) : "") +
        seed;
      fetch(url)
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data && el.getAttribute("data-cc-title"))
            data.title = el.getAttribute("data-cc-title");
          renderRow(el, key, data);
        })
        .catch(function () {});
    });
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", init);
  else init();
})();
